import { useEffect, useState } from 'react'

import { isAvatarImageLoaded, preloadAvatarImage } from './utils/preloadAvatarImage'

export type UseAvatarImageResult = {
  src: string | undefined
  isImagePending: boolean
}

/**
 * Returns `src` immediately and `isImagePending` while the image preloads.
 * Pair with `shouldShowProfileAvatar` to avoid rendering fallback during load.
 */
export function useAvatarImage(src: string | undefined): UseAvatarImageResult {
  const [isImagePending, setIsImagePending] = useState(() => Boolean(src && !isAvatarImageLoaded(src)))

  useEffect(() => {
    if (!src) {
      setIsImagePending(false)
      return
    }

    if (isAvatarImageLoaded(src)) {
      setIsImagePending(false)
      return
    }

    setIsImagePending(true)

    let cancelled = false

    void preloadAvatarImage(src)
      .then(() => {
        if (!cancelled) {
          setIsImagePending(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsImagePending(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [src])

  return { src, isImagePending }
}
